/**
 * Per-object HP bars (plan 09 P9.5): which crates and obstacles show a bar, and where each lands on
 * screen. Pure — `hpBars` is a function of the snapshot (no clock, so a paused frame shows the same
 * bars) and `projectHpBars` of the camera matrix; the pooled DOM lives in hud.ts. Enemy blocks get
 * no bar: their headcount is the HUD M (P1.5). The boss keeps its own bar.
 */
import { Matrix, Vector3, Viewport } from '@babylonjs/core/pure';
import { BLASTS, type LevelSpec } from '../sim/level';
import type { SimState } from '../sim/sim';
import { BARREL_DIMS } from './barrelDims';
import { BRIDGE_HALF_WIDTH } from './world';

/** Metres ahead of the squad within which every live crate/obstacle shows its bar, full or not. */
export const HP_BAR_RANGE = 40;
/** DOM pool size; nearest first wins (L2 never has more than 4 candidates inside 40 m). */
export const HP_BAR_POOL = 8;
/** Bar height above the deck per prop (m): just over each mesh's top. Plan 14 P14.2: the barrel's
 * bar is authored height + clearance (1.2 m drum + lid/bungs → 1.7), so it follows `BARREL_DIMS`. */
const BAR_Y = { barrel: BARREL_DIMS.height + 0.5, barricade: 1.7, crate: 1.4 } as const;
/** Bar width = the projected span of ±BAR_HALF_M, clamped to [MIN, MAX] px. */
const BAR_HALF_M = 0.6;
const BAR_MIN_PX = 24;
const BAR_MAX_PX = 72;

export interface HpBar {
  key: string;
  /** World position (m). */
  x: number;
  y: number;
  z: number;
  /** HP fraction in [0, 1]: the fill length. */
  frac: number;
  /** Fill colour = kind (explosive vs wall/crate), never the only cue. */
  explosive: boolean;
}

export interface ScreenBar {
  key: string;
  /** CSS px of the bar's top-left corner and its width. */
  left: number;
  top: number;
  width: number;
  frac: number;
  explosive: boolean;
}

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/** The bars to show this frame, nearest first. Max hp comes from the level spec, index-aligned. */
export function hpBars(sim: SimState, level: LevelSpec): HpBar[] {
  if (sim.phase !== 'run') return [];
  const near = (z: number): boolean => z - sim.squadZ > 0 && z - sim.squadZ <= HP_BAR_RANGE;
  const bars: HpBar[] = [];
  sim.crates.forEach((c, i) => {
    if (c.broken || !near(c.z)) return;
    // Crate hp can go negative (no clamp in the sim), hence clamp01.
    bars.push({ key: `crate-${i}`, x: c.x * BRIDGE_HALF_WIDTH, y: BAR_Y.crate, z: c.z, frac: clamp01(c.hp / level.crates[i].hp), explosive: false });
  });
  sim.obstacles.forEach((o, i) => {
    if (o.broken || !near(o.z)) return;
    bars.push({
      key: `obs-${i}`,
      x: o.x * BRIDGE_HALF_WIDTH,
      y: BAR_Y[o.kind],
      z: o.z,
      frac: clamp01(o.hp / level.obstacles[i].hp),
      explosive: BLASTS[o.kind] !== undefined,
    });
  });
  return bars.sort((a, b) => a.z - b.z).slice(0, HP_BAR_POOL);
}

// scratchP holds the source point only (ProjectToRef reads it, never retains it); scratchA/scratchB
// are the projection outputs. projectHpBars does not call projectPoint, so one source scratch is
// lifetime-safe in both.
const scratchP = new Vector3();
const scratchA = new Vector3();
const scratchB = new Vector3();

/** Height (m) of the hostiles readout over an enemy block: clear of the helmets (~1.8 m). */
export const ENEMY_LABEL_Y = 2.4;

/** CSS px of the world point (x, y, z) — the anchor the HUD's hostiles readout sits on. */
export function projectPoint(x: number, y: number, z: number, viewProj: Matrix, w: number, h: number): { left: number; top: number } {
  Vector3.ProjectToRef(scratchP.set(x, y, z), Matrix.IdentityReadOnly, viewProj, new Viewport(0, 0, w, h), scratchA);
  return { left: scratchA.x, top: scratchA.y };
}

/**
 * Screen placement in CSS px (`w` × `h` = the canvas client size; the viewport is the camera's
 * default full one). x/y only: the projected z depends on the backend's NDC range, and nothing
 * here is ever behind the camera (bars sit ahead of the squad, the camera 8 m behind it).
 * `viewProj` must be this frame's view × projection (see main.ts).
 */
export function projectHpBars(bars: HpBar[], viewProj: Matrix, w: number, h: number): ScreenBar[] {
  const viewport = new Viewport(0, 0, w, h);
  return bars.map((bar) => {
    Vector3.ProjectToRef(scratchP.set(bar.x - BAR_HALF_M, bar.y, bar.z), Matrix.IdentityReadOnly, viewProj, viewport, scratchA);
    Vector3.ProjectToRef(scratchP.set(bar.x + BAR_HALF_M, bar.y, bar.z), Matrix.IdentityReadOnly, viewProj, viewport, scratchB);
    const width = Math.max(BAR_MIN_PX, Math.min(BAR_MAX_PX, Math.hypot(scratchB.x - scratchA.x, scratchB.y - scratchA.y)));
    const cx = (scratchA.x + scratchB.x) / 2;
    const cy = (scratchA.y + scratchB.y) / 2;
    return { key: bar.key, left: cx - width / 2, top: cy, width, frac: bar.frac, explosive: bar.explosive };
  });
}
